/** ONE formatting vocabulary for the whole UI (U4). Every page renders time,
 *  counts, and bytes through these — never a local copy (UI law: check lib/
 *  first; a second hand-built copy is a review failure). */

/** A DURATION in seconds as "32s" / "5m" / "3h" / "2d".
 *
 *  Extracted 2026-10-18 because this ladder was already written twice — in
 *  `ago` and in `until` — and a third caller (the record table's arrival-lag
 *  tooltip) would have made three. The two existing copies had already
 *  drifted once in spirit: `ago` clamps at zero and `until` returns "now",
 *  which is a real difference in MEANING and stays in those functions, while
 *  the formatting itself has no business being duplicated. */
export function dur(seconds: number): string {
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
}

/** Relative age: "32s" / "5m" / "3h" / "2d". Dash for null. */
export function ago(iso: string | null | undefined): string {
  if (!iso) return "—";
  return dur(Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000));
}

/** How long UNTIL an instant in the future: "23h", "4m", or "now" once it has
 *  passed. The mirror of `ago`, and it exists because `ago` is deliberately
 *  clamped at zero (`Math.max(0, …)`) for a past-tense reading — so rendering
 *  a FUTURE deadline through it printed "in 0s" on a command with 24 hours
 *  left to run, which Dattatray read on the console as a command about to
 *  expire. Two directions, two functions. */
export function until(iso: string | null | undefined): string {
  if (!iso) return "—";
  const s = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  if (s <= 0) return "now";
  return dur(s);
}

/** Date + wall-clock with ms: "09 Aug 16:21:24.598" — table timestamps must
 *  carry the DATE (a history window can span days; time alone is ambiguous). */
export function dateTime(iso: string): string {
  const d = new Date(iso);
  const day = d.toLocaleDateString([], { day: "2-digit", month: "short" });
  return `${day} ${hhmmss(iso)}`;
}

/** Wall-clock with milliseconds: "16:21:24.598" (console/table timestamps). */
export function hhmmss(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour12: false }) + "." + String(d.getMilliseconds()).padStart(3, "0");
}

/** Counts auto-scale K / M / B; exact value belongs in a tooltip. */
export function fmtCount(v: number | null | undefined): string {
  if (v == null) return "—";
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 10_000) return `${(v / 1e3).toFixed(1)}K`;
  return v.toLocaleString("en-IN");
}

/** Bytes auto-scale B / KB / MB / GB. */
export function fmtBytes(v: number | null | undefined): string {
  if (v == null) return "—";
  if (v >= 1024 ** 3) return `${(v / 1024 ** 3).toFixed(2)} GB`;
  if (v >= 1024 ** 2) return `${(v / 1024 ** 2).toFixed(1)} MB`;
  if (v >= 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${v} B`;
}

/** Grouped integer (en-IN, the app's convention). */
export function fmtInt(v: number | null | undefined): string {
  return v == null ? "—" : v.toLocaleString("en-IN");
}

/** Poll cadence budget (UI performance law 1) — pick the slowest tier that
 *  matches how fast the DATA actually changes, never a faster one. */
export const POLL = {
  /** live consoles / tails — seconds-fresh views */
  CONSOLE: 2000,
  /** operational state (rollout progress, connections) */
  STATE: 5000,
  /** dashboards, aggregates, distributions */
  DASHBOARD: 15000,
  /** registry/config lists — change on human timescales */
  LIST: 15000,
} as const;
